import { Api, ApiError } from '@axiom/ui';
import { z } from 'zod';

import type { MatchGridSavedColumns } from '../../schema/spark/match-grid-column-config-api-schema';
import { CurrencyCode } from '../../enums/generated/currency-code';
import { ProfileStatus } from '../../enums/generated/profile-status';
import { EnvUtil } from '../../utils/env-util';
import { ChatState } from '../enums/chat-state';
import { MessageOriginators } from '../enums/message-originators';

/*
  The real paths, from `QPController.java` (class-level base `qualifiedPipeline`). Named rather
  than written inline so wiring a method up is one edit at the call, not a string hunt.
*/
export const CHAT_ENDPOINT = '/qualifiedPipeline';
export const CHAT_NAME_ENDPOINT = `${CHAT_ENDPOINT}/name`;
export const CHAT_UNHIDE_ENDPOINT = `${CHAT_ENDPOINT}/unhide`;
export const CHAT_CANDIDATES_ENDPOINT = `${CHAT_ENDPOINT}/candidates`;
export const CHAT_FILES_ENDPOINT = `${CHAT_ENDPOINT}/files`;
export const CHAT_COLUMN_CONFIG_ENDPOINT = `${CHAT_CANDIDATES_ENDPOINT}/columns`;

/**
 * Both ids, because the service resolves the file against the conversation it was uploaded to.
 */
export const chatFileDownloadEndpoint = ({
  chatId,
  fileId,
}: {
  chatId: string;
  fileId: string;
}) =>
  `${CHAT_FILES_ENDPOINT}/download?chatId=${encodeURIComponent(chatId)}&fileId=${encodeURIComponent(fileId)}`;

/* ───────────────────────────── response shapes ─────────────────────────────

  Mirrored from dark-roast rather than imported from `src/api/generated`, so this POC stays one
  folder to delete. Optionality is copied from the generated DTOs exactly: a response field the
  Java side does not annotate comes through `.nullish()`, which is why almost everything is.
*/

/**
 * `deleted` is the api's own name for it, and filtering is the client's job - the list query has
 * no `deleted` predicate, so hidden conversations arrive with the rest.
 */
export const chatHistoryItemSchema = z.object({
  /*
    **The 422 state, remembered.** A conversation that filled up cannot take another message ever,
    so the server says so rather than leaving every visit to find out by being refused. Nullish
    like `deleted`: a row arriving without it is a chat that still has room.
  */
  chatState: z.nativeEnum(ChatState).nullish(),
  deleted: z.boolean().nullish(),
  id: z.string().uuid(),
  name: z.string().nullish(),
});

export type ChatHistoryItem = z.infer<typeof chatHistoryItemSchema>;

export type ReadChatHistoryPayload = { data: ChatHistoryItem[] };

/**
 * A candidate the model matched to the conversation, and its reason for doing so.
 * Carried per message rather than per conversation, so a later answer can match a
 * different set.
 */
export const matchingCandidateSchema = z.object({
  candidateId: z.string().uuid().nullish(),
  matchExplanation: z.string().nullish(),
});

export type MatchingCandidate = z.infer<typeof matchingCandidateSchema>;

/**
 * **`fileType` is on the wire and deliberately not modelled**: nothing reads it (an attachment is
 * classified from its name), and hand-written code may not import `FileType` from `@axiom/domain`.
 */
export const llmFileSchema = z.object({
  fileName: z.string().nullish(),
});

export const chatMessageSchema = z.object({
  /*
    Populated but **unread**: prompt and answer are stamped together, so no stored turn has a
    duration in it. `z.string()` rather than `.datetime()` because it is a zoneless
    `LocalDateTime`, which `.datetime()` rejects - the generated schema would fail on every read.
  */
  createdAt: z.string().nullish(),
  /*
    The attachments a message was sent with, ids only. Nullish for the reason
    `matchingCandidates` is, two fields down: the generated schema says a plain array, and this
    file has already been burnt once by trusting that over what the wire actually sends.
  */
  fileIds: z.array(z.string()).nullish(),
  id: z.string().uuid(),
  /*
    **Nullish because the server sends `null`, not `[]`**, for an answer that matched nobody -
    which is most of them. This said `z.array()` once, which made lint call a correct `?.` guard
    dead code; deleting it threw on the next load. A lint rule is only as right as the schema.
  */
  matchingCandidates: z.array(matchingCandidateSchema).nullish(),
  messageOriginator: z.nativeEnum(MessageOriginators).nullish(),
  text: z.string().nullish(),
});

export type ChatMessage = z.infer<typeof chatMessageSchema>;

/**
 * The prompt is not echoed back, so the client keeps its own copy of what it sent.
 */
export const chatWithMessageSchema = z.object({
  id: z.string().uuid(),
  message: chatMessageSchema.nullish(),
  name: z.string().nullish(),
});

export type ChatWithMessage = z.infer<typeof chatWithMessageSchema>;

export const chatWithMessagesSchema = z.object({
  id: z.string().uuid(),
  messages: z.array(chatMessageSchema),
  name: z.string().nullish(),
  /*
    Every file the conversation holds, by id - where a message's attachments get their names, since
    a message carries only ids. Nullish because a conversation with no files sends no map.
  */
  openAiFiles: z.record(z.string(), llmFileSchema).nullish(),
});

export type ChatWithMessages = z.infer<typeof chatWithMessagesSchema>;

/**
 * `id` absent starts a chat, present continues it. **`lastKnownMessageId` is required in
 * practice**: omit it and any conversation with an answer in it 409s every follow-up.
 */
export const sendChatMessageSchema = z.object({
  /*
    Required and plain, unlike the read's: this is what we send, so it is always an array - `[]`
    for a prompt with nothing attached.
  */
  fileIds: z.array(z.string()),
  id: z.string().uuid().optional(),
  lastKnownMessageId: z.string().uuid().nullish(),
  text: z.string().min(1),
});

export type SendChatMessageBody = z.infer<typeof sendChatMessageSchema>;

export const readCandidatesSchema = z.object({
  candidateIds: z.array(z.string().uuid()).nullable(),
});

export type ReadCandidatesBody = z.infer<typeof readCandidatesSchema>;

/**
 * Hand-written: `sync-entities` types this endpoint's rows as `Array<Record<string, unknown>>`.
 * Use the generated response instead when it stops.
 */
export const candidateActiveEngagementSchema = z.object({
  accountName: z.string().nullish(),
  id: z.string().uuid(),
  jobName: z.string().nullish(),
  position: z
    .object({
      billingHoursPerDay: z.number().nullish(),
      billingUnitsPerWeek: z.number().nullish(),
      endDate: z.string().nullish(),
      endDateStatus: z.string().nullish(),
      reservedType: z.string().nullish(),
      weeklyTimeUnit: z.string().nullish(),
    })
    .nullish(),
});

export type CandidateActiveEngagement = z.infer<
  typeof candidateActiveEngagementSchema
>;

export const candidateDetailSchema = z.object({
  availability: z.string().nullish(),
  id: z.string().uuid(),
  activeEngagements: z.array(candidateActiveEngagementSchema).nullish(),
  availabilityPreferencesUpdatedAt: z.string().nullish(),
  /*
    Ids, not names: the payload carries the join key and the names come from `/barredLocations`,
    which is read once for the screen rather than repeated on every row.
  */
  barredLocationIds: z.array(z.string().uuid()).nullish(),
  calculatedDesiredWeeklyHours: z.number().nullish(),
  calculatedEngagedAndReservedHours: z
    .object({
      totalEngagedHours: z.number().nullable(),
      totalReservedHours: z.number().nullable(),
    })
    .nullish(),
  soonestEngagementEndDate: z.string().nullish(),
  soonestEngagementEndDateStatus: z.string().nullish(),
  weeklyAvailability: z.number().nullish(),
  calculatedDisplayName: z.string().nullish(),
  compensation: z.number().nullish(),
  currencyType: z.nativeEnum(CurrencyCode).nullish(),
  /*
    Already chosen by the server: the personal address if there is one, the work address
    otherwise, and null rather than an empty string when there is neither. Same for `phone`.
  */
  email: z.string().nullish(),
  fullTimeHourlyRate: z.number().nullish(),
  isFlagged: z.boolean().nullish(),
  minWeeklyHoursPerPosition: z.number().nullish(),
  npsScore: z.number().nullish(),
  numberOfFeedbackResponses: z.number(),
  // Already a full name: the server joins the owner's first and last before sending it.
  ownerUser: z.string().nullish(),
  phone: z.string().nullish(),
  proactiveSubmission: z.boolean(),
  profileStatus: z.nativeEnum(ProfileStatus).nullish(),
  // Already joined by the server: "City, State", or whichever of the two it has.
  talentLocation: z.string().nullish(),
  variableHourlyRate: z.number().nullish(),
  yearsOfExperience: z.number().nullish(),
  yearsOnAxiomEngagements: z.number(),
});

export type CandidateDetail = z.infer<typeof candidateDetailSchema>;

const CHAT_NAME_MAX_LENGTH = 40;

export const renameChatSchema = z.object({
  id: z.string().uuid(),
  name: z.string().min(1).max(CHAT_NAME_MAX_LENGTH),
});

export type RenameChatBody = z.infer<typeof renameChatSchema>;

export const updateChatVisibilitySchema = z.object({
  id: z.string().uuid(),
});

export type UpdateChatVisibilityBody = z.infer<
  typeof updateChatVisibilitySchema
>;

export const renamedChatSchema = z.object({
  id: z.string().uuid(),
  name: z.string().nullish(),
});

export type RenamedChat = z.infer<typeof renamedChatSchema>;

export type RenameChatPayload = { data: RenamedChat };

/**
 * **`chatId` is the surprising one**: the query parameter is optional, and without it the endpoint
 * creates a conversation and returns its id.
 */
export const uploadedFileSchema = z.object({
  chatId: z.string().uuid().nullish(),
  file: llmFileSchema.nullish(),
  fileId: z.string().nullish(),
});

export type UploadedFile = z.infer<typeof uploadedFileSchema>;

export type UploadFilePayload = { data: UploadedFile };

export type WriteChatPayload = { data: ChatWithMessage };
export type ReadChatPayload = { data: ChatWithMessages };
export type ReadCandidatesPayload = { data: CandidateDetail[] };

/**
 * The columns the reader has turned on and the ones they have turned off, each in the order they
 * sit in. One row per reader, which is why the write is a create the first time and an update
 * every time after.
 */
export type ColumnConfig = MatchGridSavedColumns;

export type WriteColumnConfigBody = ColumnConfig;

// A reader who never saved one gets a 404, not an empty list.
export type ReadColumnConfigPayload = { data: ColumnConfig };
export type WriteColumnConfigPayload = { data: ColumnConfig };

class ChatApiClass extends Api {
  constructor() {
    super({
      domain: EnvUtil.apiEnvoyUrl,
      useFetch: true,
    });
  }

  readChatHistory() {
    return super.read<ReadChatHistoryPayload>({
      endpoint: CHAT_ENDPOINT,
      method: 'GET',
    });
  }

  readColumnConfig() {
    return super.read<ReadColumnConfigPayload>({
      endpoint: CHAT_COLUMN_CONFIG_ENDPOINT,
      method: 'GET',
    });
  }

  /** The reader's first save. A second one is refused: the row already exists. */
  async createColumnConfig({ disabledColumns, enabledColumns }: ColumnConfig) {
    return super.write<WriteColumnConfigPayload>({
      disableBlocker: true,
      body: {
        disabledColumns,
        enabledColumns,
      } satisfies WriteColumnConfigBody,
      endpoint: CHAT_COLUMN_CONFIG_ENDPOINT,
      method: 'POST',
    });
  }

  /** Every save after the first. The whole list each time - the server replaces, it does not merge. */
  async updateColumnConfig({ disabledColumns, enabledColumns }: ColumnConfig) {
    return super.write<WriteColumnConfigPayload>({
      disableBlocker: true,
      body: {
        disabledColumns,
        enabledColumns,
      } satisfies WriteColumnConfigBody,
      endpoint: CHAT_COLUMN_CONFIG_ENDPOINT,
      method: 'PUT',
    });
  }

  /**
   * **`clear`, not `invalidate`.** A write never invalidates a read, so the next mount would be
   * served the configuration as it was before the save. `invalidate` re-issues the request there
   * and then, which on the way out fetches and caches an answer nobody is waiting for - `clear`
   * drops the entry and leaves the next mount to ask.
   */
  forgetColumnConfig() {
    super.clear(CHAT_COLUMN_CONFIG_ENDPOINT);
  }

  /**
   * **Nothing else invalidates this list.** A write never invalidates a read, so hiding, renaming
   * or minting a conversation leaves the cached list describing the one before it - and the next
   * read of it, this visit or the next, is served that cache rather than the server.
   */
  async refreshChatHistory() {
    return super.invalidate(CHAT_ENDPOINT);
  }

  /**
   * **`clear`, not `invalidate`.** `invalidate` does not mark the entry stale, it re-issues the
   * request there and then - so on the way out it would fetch the list and cache that, and a
   * reader returning an hour later would be served an hour-old answer. `clear` drops the entry
   * with no request, leaving the next read to go to the server.
   *
   * Called when the screen unmounts, for the reason a 409 exists: a list edited elsewhere since is
   * not merely old, it is wrong.
   */
  forgetChatHistory() {
    super.clear(CHAT_ENDPOINT);
  }

  readChat({ chatId }: { chatId: ChatWithMessages['id'] }) {
    return super.read<ReadChatPayload>({
      endpoint: `${CHAT_ENDPOINT}/${chatId}`,
      method: 'GET',
    });
  }

  /**
   * **Not a nicety**: a write never invalidates a read, so without this a chat you leave and come
   * back to answers from cache, with any prompts sent in between missing.
   */
  async refreshChat({ chatId }: { chatId: ChatWithMessages['id'] }) {
    return super.invalidate(`${CHAT_ENDPOINT}/${chatId}`);
  }

  /**
   * **Do not simplify to `toPromise()`**: on a revisit the cache is warm, `watch()` fires no
   * request, and `toPromise()` waits on a `Subject` that never emits again - the read succeeds and
   * the promise never settles. `refetch()` raises the app-wide `Blocker` where `super.invalidate`
   * bypasses it, and both hold only while our reads pass no `schema:` and so stay on the legacy
   * `EndpointCache`.
   */
  async loadChat({ chatId }: { chatId: ChatWithMessages['id'] }) {
    await this.refreshChat({ chatId });

    const watcher = this.readChat({ chatId });

    /*
      **Annotated wider than the design system's own types.** A read that found nothing resolves
      `null` rather than rejecting - observed against a 404 - but `getData()`/`toPromise()` are
      typed non-nullable, so without this annotation the guard below reads as dead code to
      `no-unnecessary-condition` and gets deleted. Both callers destructure `data`, so losing it
      turns a load failure into a TypeError about destructuring `null`.
    */
    const payload = (watcher.getData() ??
      (await watcher.toPromise())) as ReadChatPayload | null;

    if (!payload) {
      throw new Error(`ChatApi: no conversation found for ${chatId}`);
    }

    return payload;
  }

  /**
   * A `read` despite being a POST, because it fetches rather than mutates and `AsyncConnector`
   * needs a watcher. **Send `null`, never `[]`**: the service guards on `== null`, so an empty
   * array reaches Elasticsearch as no filter and answers with the first thirty candidates it
   * holds. Capped at 30 with no way to ask for more.
   */
  readCandidates({ candidateIds }: { candidateIds: string[] | null }) {
    return super.read<ReadCandidatesPayload>({
      body: {
        candidateIds,
      } satisfies ReadCandidatesBody,
      endpoint: CHAT_CANDIDATES_ENDPOINT,
      method: 'POST',
    });
  }

  /**
   * **An upload with no `chatId` mints the conversation**, so adopt the id from this response
   * before sending or you get two. Not the generated `createQualifiedPipelineFiles`: it takes no
   * body, so it would upload nothing.
   */
  async uploadFile({
    chatId,
    file,
  }: {
    chatId: ChatHistoryItem['id'] | null;
    file: File;
  }) {
    const body = new FormData();

    body.append('file', file);

    return super.write<UploadFilePayload>({
      disableBlocker: true,
      body,
      endpoint: CHAT_FILES_ENDPOINT,
      method: 'POST',
      // Omitted rather than null: the server branches on the parameter being absent.
      ...(chatId ? { query: { chatId } } : {}),
    });
  }

  /**
   * **A raw `fetch`, and it has to be**: `api-fetch-request` does `await response.text()` on every
   * response, which decodes bytes and ruins them. `credentials: 'include'` because auth is a
   * cookie - which is also why the download needs no fetch at all, just an `<a href download>`.
   */
  async readAttachment({ chatId, fileId }: { chatId: string; fileId: string }) {
    const response = await fetch(
      `${EnvUtil.apiEnvoyUrl}${chatFileDownloadEndpoint({ chatId, fileId })}`,
      { credentials: 'include' }
    );

    if (!response.ok) {
      throw new ApiError({
        endpoint: chatFileDownloadEndpoint({ chatId, fileId }),
        method: 'GET',
        status: response.status,
      });
    }

    return response.blob();
  }

  /**
   * One method because it is one route: the server starts or continues a conversation by whether
   * the body carries an `id`. **Omit `lastKnownMessageId` and every message after the first 409s**
   * - `UUID.equals(null)` is false against the last answer the server finds.
   */
  async sendChat({
    chatId = null,
    fileIds = [],
    lastKnownMessageId = null,
    prompt,
  }: {
    chatId: ChatWithMessage['id'] | null;
    fileIds?: string[];
    lastKnownMessageId?: string | null;
    prompt: string;
  }) {
    return super.write<WriteChatPayload>({
      disableBlocker: true,
      body: {
        fileIds,
        lastKnownMessageId,
        text: prompt,
        // A spread, because the field is `.optional()` and not nullish: a create omits the key.
        ...(chatId ? { id: chatId } : {}),
      } satisfies SendChatMessageBody,
      endpoint: CHAT_ENDPOINT,
      method: 'PUT',
    });
  }

  async renameChat({
    chatId,
    name,
  }: {
    chatId: ChatHistoryItem['id'];
    name: string;
  }) {
    return super.write<RenameChatPayload>({
      disableBlocker: true,
      body: { id: chatId, name } satisfies RenameChatBody,
      endpoint: CHAT_NAME_ENDPOINT,
      method: 'PUT',
    });
  }

  async hideChat({ chatId }: { chatId: ChatHistoryItem['id'] }) {
    return super.write<{ data: null }>({
      endpoint: `${CHAT_ENDPOINT}/${chatId}`,
      disableBlocker: true,
      method: 'DELETE',
    });
  }

  async updateChatVisibility({ chatId }: { chatId: ChatHistoryItem['id'] }) {
    return super.write<{ data: null }>({
      body: { id: chatId } satisfies UpdateChatVisibilityBody,
      disableBlocker: true,
      endpoint: CHAT_UNHIDE_ENDPOINT,
      method: 'PATCH',
    });
  }
}

export const ChatApi = new ChatApiClass();
